import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ManagerService } from 'src/app/core';
import { LocalService } from 'src/app/core';
import { CONSTANTS } from 'src/app/config/constants';

@Component({
  selector: 'app-leader-create-challenge',
  templateUrl: './leader-create-challenge.component.html',
  styleUrls: ['./leader-create-challenge.component.scss']
})
export class LeaderCreateChallengeComponent implements OnInit, OnChanges {
  @Input() editChallengeData: any = null;

  @Output() openDescription = new EventEmitter<void>();
  @Output() submitBooking = new EventEmitter<{ formValue: any, updateFlag: boolean, selectedChallengeType: any }>();
  @Output() submitArchitecture = new EventEmitter<{ formValue: any, updateFlag: boolean, selectedChallengeType: any }>();
  @Output() submitTraining = new EventEmitter<{
    formValue: any, updateFlag: boolean, selectedChallengeType: any, selectedLearningMapsCourses: any[]
  }>();

  allChallengeTypes: any[] = [];
  selectedChallengeType: any;
  allUsers: any[] = [];
  challengeParticipantFlag: boolean = false;
  totalCompanyCreditsAvailable: any;
  errMessages = CONSTANTS.validationMessages.manageActivity;
  minDate = new Date();
  minEndDate = new Date();
  maxDate: Date | null = null;
  updateFlag: boolean = false;

  isBookingChallenge: boolean = false;
  bookingChallengeForm!: FormGroup;
  bookingTicketError: string = '';

  isArchitectureChallenge: boolean = false;
  architectureChallengeForm!: FormGroup;
  allArchitectureTypes: any;
  architectureTicketError: string = '';

  isEnablementChallenge: boolean = false;
  enablementChallengeForm!: FormGroup;
  trainingTicketError: string = '';
  isLearningMapsReady: boolean = false;
  isLearningCoursesReady: boolean = false;
  learningMapsCourses: any[] = [
    { id: 'Learning Maps', name: 'Learning Maps', children: [] },
    { id: 'Continuous Learning', name: 'Continuous Learning', children: [] },
  ];
  selectedLearningMapsCourses: any[] = [];

  private userProfile: any;

  get bookingWinnersOptions(): number[] {
    const control = this.bookingChallengeForm.get('users');
    const users = control ? control.value : null;
    if (users && users.length > 0) {
      return Array.from({ length: users.length }, (_, i) => i + 1);
    }
    return [];
  }

  get architectureWinnersOptions(): number[] {
    const control = this.architectureChallengeForm.get('users');
    const users = control ? control.value : null;
    if (users && users.length > 0) {
      return Array.from({ length: users.length }, (_, i) => i + 1);
    }
    return [];
  }

  get enablementWinnersOptions(): number[] {
    const control = this.enablementChallengeForm.get('users');
    const users = control ? control.value : null;
    if (users && users.length > 0) {
      return Array.from({ length: users.length }, (_, i) => i + 1);
    }
    return [];
  }

  constructor(private formBuilder: FormBuilder, private managerService: ManagerService) {}

  ngOnInit() {
    this.userProfile = LocalService.get('userProfile');
    this.initForms();
    this.getAllChallengeTypes();
    this.getArchitectureTypes();
    this.getUsersUnderManager();
    this.getCompanyCreditsAvailable();
  }

  getArchitectureTypes() {
    this.managerService.getArchitectureTypes().subscribe((data: any) => {
      this.allArchitectureTypes = data;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['editChallengeData'] && this.editChallengeData && this.bookingChallengeForm) {
      this.selectedChallenge(this.editChallengeData.type, this.editChallengeData);
    }
  }

  private initForms() {
    this.bookingChallengeForm = this.formBuilder.group({
      startDate: ['', [Validators.required]],
      completeBy: ['', [Validators.required]],
      winnersCount: ['', [Validators.required]],
      credits: ['', [Validators.required, Validators.pattern(CONSTANTS.regex.numeric)]],
      bookingsToBeSold: ['', [Validators.required, Validators.pattern(CONSTANTS.regex.notZero)]],
      users: ['', [Validators.required]],
      typeId: [''],
      name: ['', [Validators.required, Validators.maxLength(50)]],
    });

    this.architectureChallengeForm = this.formBuilder.group({
      archTypeAndAmountGroup: this.formBuilder.array([this.addArchTypeAndAmountGroup()]),
      startDate: ['', [Validators.required]],
      completeBy: ['', [Validators.required]],
      credits: ['', [Validators.required, Validators.pattern(CONSTANTS.regex.numeric)]],
      users: ['', [Validators.required]],
      winnersCount: ['', [Validators.required]],
      typeId: [''],
      name: ['', [Validators.required, Validators.maxLength(50)]],
    });

    this.enablementChallengeForm = this.formBuilder.group({
      startDate: [{ value: '', disabled: true }, [Validators.required]],
      completeBy: [{ value: '', disabled: true }, [Validators.required]],
      credits: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(CONSTANTS.regex.numeric)]],
      users: [{ value: null, disabled: true }, [Validators.required]],
      winnersCount: [{ value: '', disabled: true }, [Validators.required]],
      typeId: [{ value: '', disabled: true }],
      name: [{ value: '', disabled: true }, [Validators.required, Validators.maxLength(50)]],
    });
  }

  getAllChallengeTypes() {
    const lang = localStorage.getItem('selectedLanguage');
    this.managerService.getAllChallengeTypes(false).subscribe((data: any) => {
      this.allChallengeTypes = data.reduce((activeChallenges: any, item: any) => {
        activeChallenges.push({ id: item.id, name: item.name, shortName: item.shortName.toLowerCase() });
        return activeChallenges;
      }, []);
      if (lang === 'Korean' || lang === 'Japanese') {
        Object.keys(this.allChallengeTypes).forEach((key: any) => {
          if (this.allChallengeTypes[key]['name'] === 'Bookings Challenge') {
            this.allChallengeTypes[key]['name'] = lang === 'Korean' ? '부킹 챌린지' : 'ブッキングチャレンジ';
          }
          if (this.allChallengeTypes[key]['name'] === 'Architecture Challenge') {
            this.allChallengeTypes[key]['name'] = lang === 'Korean' ? '아키텍처 챌린지' : 'アーキテクチャチャレンジ';
          }
          if (this.allChallengeTypes[key]['name'] === 'Training Challenge') {
            this.allChallengeTypes[key]['name'] = lang === 'Korean' ? '트레이닝 챌린지' : 'トレーニングチャレンジ';
          }
        });
      }
    });
  }

  getUsersUnderManager() {
    this.managerService.getUsersUnderManager(this.userProfile.id).subscribe((data: any) => {
      if (data) {
        this.allUsers = data.reduce((users: any, item: any) => {
          if (users.find((obj: any) => obj.id === item.id) === undefined) {
            users.push({ id: item.id, fullName: `${item.firstName} ${item.lastName} (${item.username})` });
          }
          return users;
        }, []);
      }
    });
  }

  getCompanyCreditsAvailable() {
    this.managerService.getCompanyCreditsAvailable(this.userProfile.company.id).subscribe((data: any) => {
      this.totalCompanyCreditsAvailable = data.creditsRemaining;
    });
  }

  selectedChallenge(item: any, challenge?: any) {
    this.updateFlag = false;
    this.bookingChallengeForm.reset();
    this.architectureChallengeForm.reset();
    this.enablementChallengeForm.reset();
    this.minEndDate = new Date();
    this.maxDate = null;
    this.isBookingChallenge = item.shortName === 'bookings';
    this.isArchitectureChallenge = item.shortName === 'architecture';
    this.isEnablementChallenge = item.shortName === 'training';
    this.challengeParticipantFlag = true;
    this.selectedLearningMapsCourses = [];
    this.selectedChallengeType = item;
    if (challenge) {
      this.updateFlag = true;
      switch (item.shortName) {
        case 'bookings': {
          this.bookingChallengeForm.patchValue(challenge, { onlySelf: true });
          this.bookingChallengeForm.patchValue({ bookingsToBeSold: challenge.bookingsToBeSold });
          this.bookingChallengeForm.patchValue({ credits: challenge.credits });
          this.bookingChallengeForm.patchValue({ users: this.getSelectedUsers(challenge) });
          this.bookingChallengeForm.patchValue({ startDate: challenge.startDate });
          this.bookingChallengeForm.patchValue({ completeBy: challenge.completeBy });
          this.bookingChallengeForm.patchValue({ winnersCount: challenge.winnersCount });
          break;
        }
        case 'architecture': {
          this.architectureChallengeForm.patchValue(challenge, { onlySelf: true });
          this.architectureChallengeForm.patchValue({ users: this.getSelectedUsers(challenge) });
          this.architectureChallengeForm.patchValue({ credits: challenge.credits });
          this.architectureChallengeForm.patchValue({ startDate: challenge.startDate });
          this.architectureChallengeForm.patchValue({ completeBy: challenge.completeBy });
          for (let i = 0; i < challenge.architectureTargets.length; i++) {
            if (i === 0) {
              this.getArchTypeAndBookingControl()[i].get('architectureBookingsToBeSold')!
                .setValue(challenge.architectureTargets[i].architectureBookingsToBeSold);
              this.getArchTypeAndBookingControl()[i].get('architectureTypeId')!
                .setValue(challenge.architectureTargets[i].architectureTypeIds);
            } else {
              this.archTypeAndAmountGroupArray.push(this.formBuilder.group({
                architectureTypeId: [challenge.architectureTargets[i].architectureTypeIds, [Validators.required]],
                architectureBookingsToBeSold: [challenge.architectureTargets[i].architectureBookingsToBeSold, Validators.required],
              }));
            }
          }
          this.architectureChallengeForm.patchValue({ winnersCount: challenge.winnersCount });
          break;
        }
        case 'training': {
          this.enablementChallengeForm.patchValue(challenge, { onlySelf: true });
          this.enablementChallengeForm.patchValue({ users: this.getSelectedUsers(challenge) });
          this.enablementChallengeForm.patchValue({ startDate: challenge.startDate });
          this.enablementChallengeForm.patchValue({ completeBy: challenge.completeBy });
          challenge.courseIdsDTOS.map((course: any) => {
            this.selectedLearningMapsCourses.push({ id: course.courseId + '-c' });
          });
          challenge.learningMapIdsDTOS.map((course: any) => {
            this.selectedLearningMapsCourses.push({ id: course.learningMapId + '-m' });
          });
          this.enablementChallengeForm.patchValue({ credits: challenge.credits });
          this.enablementChallengeForm.patchValue({ winnersCount: challenge.winnersCount });
          break;
        }
      }
      this.getFiscalPeriod(challenge.startDate);
    }
  }

  getFiscalPeriod(date: any) {
    this.minEndDate = date;
    const queryParams = { startDate: new Date(date).toISOString() };
    this.managerService.getFiscalPeriod(queryParams).subscribe((data: any) => {
      this.maxDate = new Date(data.endDate);
    });
  }

  // Booking
  bookingTicketCalculation() {
    this.bookingTicketError = '';
    const numberOfWinners = this.bookingChallengeForm.value.winnersCount;
    const credits = this.bookingChallengeForm.value.credits;
    if (Number(numberOfWinners) && Number(credits)) {
      const tickets = numberOfWinners * credits;
      if (Number(tickets) > Number(this.totalCompanyCreditsAvailable) && tickets !== 0) {
        const allowed: any = this.totalCompanyCreditsAvailable;
        const allowedTicket = allowed / numberOfWinners > 0 ? allowed / numberOfWinners : 0;
        this.bookingTicketError = 'The tickets to each winner should not be more than ' + Math.floor(allowedTicket);
      }
    }
  }

  onBookingStartDateChange(date: Date) {
    this.getFiscalPeriod(date);
  }

  resetBookingChallengeForm() {
    this.minEndDate = new Date();
    this.maxDate = null;
    this.bookingChallengeForm.reset();
    this.selectedChallengeType = null;
    this.isBookingChallenge = false;
  }

  postBookingChallengeForm(updateFlag: boolean) {
    this.submitBooking.emit({
      formValue: this.bookingChallengeForm.value,
      updateFlag: updateFlag,
      selectedChallengeType: this.selectedChallengeType,
    });
  }

  // Architecture
  architectureTicketCalculation() {
    this.architectureTicketError = '';
    const numberOfWinners = this.architectureChallengeForm.value.winnersCount;
    const credits = this.architectureChallengeForm.value.credits;
    if (Number(numberOfWinners) && Number(credits)) {
      const tickets = numberOfWinners * credits;
      if (Number(tickets) > Number(this.totalCompanyCreditsAvailable) && tickets !== 0) {
        const allowed: any = this.totalCompanyCreditsAvailable;
        const allowedTicket = allowed / numberOfWinners > 0 ? allowed / numberOfWinners : 0;
        this.architectureTicketError = 'The tickets to each winner should not be more than ' + Math.floor(allowedTicket);
      }
    }
  }

  onArchitectureStartDateChange(date: Date) {
    this.getFiscalPeriod(date);
  }

  resetArchitectureChallengeForm() {
    this.minEndDate = new Date();
    this.maxDate = null;
    this.architectureChallengeForm.reset();
    this.selectedChallengeType = null;
    this.isArchitectureChallenge = false;
  }

  postArchitectureChallengeForm(updateFlag: boolean) {
    this.submitArchitecture.emit({
      formValue: this.architectureChallengeForm.value,
      updateFlag: updateFlag,
      selectedChallengeType: this.selectedChallengeType,
    });
  }

  // Training
  trainingTicketCalculation() {
    this.trainingTicketError = '';
    const numberOfWinners = this.enablementChallengeForm.value.winnersCount;
    const credits = this.enablementChallengeForm.value.credits;
    if (Number(numberOfWinners) && Number(credits)) {
      const tickets = numberOfWinners * credits;
      if (Number(tickets) > Number(this.totalCompanyCreditsAvailable) && tickets !== 0) {
        const allowed: any = this.totalCompanyCreditsAvailable;
        const allowedTicket = allowed / numberOfWinners > 0 ? allowed / numberOfWinners : 0;
        this.trainingTicketError = 'The tickets to each winner should not be more than ' + Math.floor(allowedTicket);
      }
    }
  }

  onEnablementStartDateChange(date: Date) {
    this.getFiscalPeriod(date);
  }

  resetEnablementChallengeForm() {
    this.minEndDate = new Date();
    this.maxDate = null;
    this.enablementChallengeForm.reset();
    this.selectedChallengeType = null;
    this.isEnablementChallenge = false;
  }

  postEnablementChallengeForm(updateFlag: boolean) {
    this.submitTraining.emit({
      formValue: this.enablementChallengeForm.getRawValue(),
      updateFlag: updateFlag,
      selectedChallengeType: this.selectedChallengeType,
      selectedLearningMapsCourses: this.selectedLearningMapsCourses,
    });
  }

  // Architecture form array helpers
  addArchTypeAndAmountGroup() {
    return this.formBuilder.group({
      architectureTypeId: [[], Validators.required],
      architectureBookingsToBeSold: ['', [Validators.required]],
    });
  }

  get archTypeAndAmountGroupArray(): FormArray {
    return <FormArray>this.architectureChallengeForm.get('archTypeAndAmountGroup');
  }

  addNewArchTypeAndAmountGroup() {
    this.archTypeAndAmountGroupArray.push(this.addArchTypeAndAmountGroup());
  }

  removeGroup(index: number) {
    this.archTypeAndAmountGroupArray.removeAt(index);
  }

  getArchTypeAndBookingControl() {
    return (this.architectureChallengeForm.get('archTypeAndAmountGroup') as FormArray).controls;
  }

  private getSelectedUsers(challenge: any) {
    return this.allUsers.filter((data: any) => {
      return challenge.userIds.find((item: any) => item === data.id);
    });
  }
}
